import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../../stores/useAppStore';
import { Header, Footer, Toast } from '../../components';
import type { ToastMessage } from '../../components';
import { AppleEmoji } from '../../components/AppleEmoji';
import { orderApi, publicApi } from '../../services/api';
import type { PaymentMethod } from '../../services/api';
import { useAuthStore } from '../../stores/useAuthStore';
import WalletSelector from '../../components/WalletSelector/WalletSelector';
import { validateTrc20Address } from '../../utils/tron';
import './HomePage.css';

const DEFAULT_FAQ_ITEMS = [
  { q: 'Какие способы оплаты доступны?', a: 'Оплата через СБП (Система Быстрых Платежей) — быстро и без комиссий.' },
  { q: 'Как быстро приходит USDT?', a: 'USDT отправляется в течение 5-15 минут после подтверждения оплаты.' },
  { q: 'Какая минимальная сумма?', a: 'Минимальная сумма покупки зависит от текущих настроек обменника.' },
  { q: 'Осуществляете ли вы возвраты?', a: 'Да, в течение 24 часов с момента оплаты, если USDT ещё не были отправлены.' },
  { q: 'Остались вопросы?', a: 'Напишите нам в Telegram — мы на связи 24/7.' },
] as const;

export function HomePage() {
  const navigate = useNavigate();
  const {
    setUsdtAmount,
    walletAddress, setWalletAddress,
    settings, prices, paymentMethods, faqs
  } = useAppStore();

  const faqItems = faqs.length > 0 ? faqs : DEFAULT_FAQ_ITEMS;

  const [usdtInput, setUsdtInput] = useState('');
  const [rubInput, setRubInput] = useState('');
  const [inputMode, setInputMode] = useState<'usdt' | 'rub'>('usdt');
  const [wallet, setWallet] = useState(walletAddress);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState<{ discount: number; code: string } | null>(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [price, setPrice] = useState<{ base_price: number; discount: number; service_commission: number; transfer_fee: number; final_price: number } | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const authUser = useAuthStore((s) => s.user);
  const authToken = useAuthStore((s) => s.token);

  const showToast = useCallback((type: ToastMessage['type'], message: string, duration?: number) => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, type, message, duration }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Auto-select first payment method if only one
  useEffect(() => {
    if (paymentMethods.length === 1 && !paymentMethod) {
      setPaymentMethod(paymentMethods[0].code);
    }
  }, [paymentMethods, paymentMethod]);

  const handleApplyPromo = async () => {
    if (!promoCode.trim() || promoLoading) return;
    if (!price || price.final_price <= 0) {
      showToast('error', 'Сначала укажите сумму');
      return;
    }
    setPromoLoading(true);
    try {
      const res = await publicApi.validatePromo(promoCode.trim(), price.final_price);
      if (res.data.valid) {
        setPromoApplied({ discount: res.data.discount_amount, code: res.data.code });
        showToast('success', `Промокод применён! Скидка: ${res.data.discount_amount.toLocaleString('ru-RU')}₽`);
      }
    } catch {
      setPromoApplied(null);
      showToast('error', 'Промокод недействителен');
    } finally {
      setPromoLoading(false);
    }
  };

  const usdtConfigGlobal = prices['usdt'];
  const commissionPctGlobal = parseFloat(settings['service_commission_percent'] || '10');
  const transferFeeUsdtGlobal = parseFloat(settings['usdt_transfer_fee_usdt'] || '2');
  const rateGlobal = usdtConfigGlobal?.base_price ?? 97;

  const commissionsEnabled = (settings['payment_commission_enabled'] ?? 'true')
    .toString().toLowerCase().trim() !== 'false';

  const selectedMethodObj = paymentMethods.find(m => m.code === paymentMethod);

  const computePriceFromUsdt = useCallback((numUsdt: number) => {
    const basePrice = numUsdt * rateGlobal;
    let discount = 0;
    if (
      usdtConfigGlobal?.discount_threshold &&
      numUsdt >= usdtConfigGlobal.discount_threshold &&
      usdtConfigGlobal.discount_percent
    ) {
      discount = basePrice * (usdtConfigGlobal.discount_percent / 100);
    }
    const subtotal = basePrice - discount;
    const serviceCommission = commissionsEnabled ? subtotal * (commissionPctGlobal / 100) : 0;
    const transferFee = commissionsEnabled ? transferFeeUsdtGlobal * rateGlobal : 0;
    let final_price = subtotal + serviceCommission + transferFee;
    if (selectedMethodObj) {
      if (selectedMethodObj.commission_percent) {
        final_price += final_price * (selectedMethodObj.commission_percent / 100);
      }
      if (selectedMethodObj.commission_fixed) {
        final_price += selectedMethodObj.commission_fixed;
      }
    }
    return {
      base_price: basePrice,
      discount,
      service_commission: serviceCommission,
      transfer_fee: transferFee,
      final_price: Math.round(final_price),
    };
  }, [rateGlobal, commissionPctGlobal, transferFeeUsdtGlobal, usdtConfigGlobal, commissionsEnabled, selectedMethodObj]);

  // Inverse: given target final RUB price, find USDT amount
  const usdtFromRub = useCallback((rubFinal: number): number => {
    if (rubFinal <= 0 || rateGlobal <= 0) return 0;
    // Strip payment-method commission first — backend adds it on top.
    let target = rubFinal;
    if (selectedMethodObj) {
      const m = (selectedMethodObj.commission_percent || 0) / 100;
      const f = selectedMethodObj.commission_fixed || 0;
      target = (rubFinal - f) / (1 + m);
    }
    const c = commissionsEnabled ? commissionPctGlobal / 100 : 0;
    const transferFeeRub = commissionsEnabled ? transferFeeUsdtGlobal * rateGlobal : 0;
    let usdt = (target - transferFeeRub) / (rateGlobal * (1 + c));
    if (
      usdtConfigGlobal?.discount_threshold &&
      usdtConfigGlobal.discount_percent &&
      usdt >= usdtConfigGlobal.discount_threshold
    ) {
      const d = usdtConfigGlobal.discount_percent / 100;
      const candidate = (target - transferFeeRub) / (rateGlobal * (1 - d) * (1 + c));
      if (candidate >= usdtConfigGlobal.discount_threshold) {
        usdt = candidate;
      }
    }
    return Math.max(usdt, 0);
  }, [rateGlobal, commissionPctGlobal, transferFeeUsdtGlobal, usdtConfigGlobal, commissionsEnabled, selectedMethodObj]);

  // In RUB mode, re-derive USDT when commissions/method change
  useEffect(() => {
    if (inputMode !== 'rub') return;
    const numRub = parseFloat(rubInput);
    if (numRub > 0) {
      const usdt = usdtFromRub(numRub);
      setUsdtInput(usdt > 0 ? usdt.toFixed(4) : '');
    }
  }, [usdtFromRub, inputMode, rubInput]);

  // Calculate price + keep the inactive field in sync
  useEffect(() => {
    const numUsdt = parseFloat(usdtInput);
    if (numUsdt > 0) {
      const next = computePriceFromUsdt(numUsdt);
      setPrice(next);
      if (inputMode === 'usdt') {
        setRubInput(next.final_price.toFixed(2));
      }
    } else {
      setPrice(null);
      if (inputMode === 'usdt') setRubInput('');
    }
  }, [usdtInput, computePriceFromUsdt, inputMode]);

  const handlePay = async () => {
    if (!paymentMethod || isLoading) return;

    if (!authToken || !authUser) {
      showToast('info', 'Войдите в аккаунт, чтобы совершить покупку');
      navigate('/login', { state: { from: '/' } });
      return;
    }
    if (!authUser.is_fully_verified) {
      showToast('error', 'Подтвердите телефон в личном кабинете');
      navigate('/account');
      return;
    }
    if (!authUser.full_name || authUser.full_name.trim().length < 2) {
      showToast('error', 'Укажите ФИО в разделе верификации');
      navigate('/account/verification');
      return;
    }

    const walletValidation = await validateTrc20Address(wallet);
    if (!walletValidation.valid) {
      showToast('error', walletValidation.reason);
      return;
    }

    const usdtNum = parseFloat(usdtInput) || 0;
    const usdtConfig = prices['usdt'];
    const minUsdt = usdtConfig?.min_amount || 1;
    const maxUsdt = usdtConfig?.max_amount || 10000;

    if (usdtNum < minUsdt) {
      showToast('error', `Минимальное количество USDT: ${minUsdt}`);
      return;
    }
    if (usdtNum > maxUsdt) {
      showToast('error', `Максимальное количество USDT: ${maxUsdt}`);
      return;
    }

    // Check payment method min amount
    const selectedMethod = paymentMethods.find(m => m.code === paymentMethod);
    if (selectedMethod?.min_amount && price?.final_price && price.final_price < selectedMethod.min_amount) {
      showToast('error', `Минимальная сумма для ${selectedMethod.name}: ${selectedMethod.min_amount}₽`);
      return;
    }

    setUsdtAmount(usdtNum);
    setWalletAddress(wallet);
    setIsLoading(true);

    try {
      const response = await orderApi.createUsdtOrder(
        0,
        usdtNum,
        wallet,
        'TRC-20',
        paymentMethod,
        authUser?.email || undefined,
        price?.final_price
      );

      navigate(`/payment/${response.data.uuid}`);
    } catch (error: any) {
      console.error('Error creating order:', error);
      const errorMessage = error?.response?.data?.detail || 'Ошибка создания заказа. Попробуйте позже.';
      showToast('error', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const getText = (key: string, defaultValue: string) => settings[key] || defaultValue;

  const getPaymentIcon = (method: PaymentMethod) => {
    if (method.icon_url) {
      return <img src={method.icon_url} alt={method.name} className="payment-method-icon" />;
    }
    const icons: Record<string, string> = {
      sbp: '/sbp.svg',
    };
    return icons[method.code] ? (
      <img src={icons[method.code]} alt={method.name} className="payment-method-icon" />
    ) : null;
  };

  const finalPrice = price?.final_price || 0;
  const isAuthed = Boolean(authToken && authUser);
  const walletLooksValid = wallet.length === 34 && wallet.startsWith('T');
  const isFormValid = Boolean(
    walletLooksValid &&
    paymentMethod &&
    parseFloat(usdtInput) > 0
  );

  // Get rate for display
  const usdtConfig = prices['usdt'];
  const currentRate = usdtConfig?.base_price || 97;

  return (
    <div className="home-page">
      <Header />

      <main className="home-page__main">
        {/* Hero Section */}
        <section className="hero">
          <h1 className="hero__title">
            {getText('hero_title', 'Покупайте USDT за рубли — быстро и надёжно').split('USDT').map((part, i) => (
              i === 0 ? (
                <span key={i}>{part}<span className="hero__title-accent">USDT</span></span>
              ) : part
            ))}
          </h1>
          <p className="hero__subtitle">
            {getText('hero_subtitle', 'Оплата через СБП. Отправка на ваш кошелёк TRC-20.')}
          </p>

          {/* Flow illustration */}
          <div className="hero__flow" aria-hidden="true">
            <div className="hero__flow-node">
              <img src="/ruble.svg" alt="" width="40" height="40" />
              <span className="hero__flow-label">RUB</span>
              <span className="hero__flow-sub">СБП</span>
            </div>
            <div className="hero__flow-arrow">
              <svg width="48" height="16" viewBox="0 0 48 16" fill="none" aria-hidden="true">
                <path d="M1 8h42M37 2l6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="hero__flow-node hero__flow-node--accent">
              <img src={settings['icon_usdt'] || '/usdt.svg'} alt="" width="40" height="40" />
              <span className="hero__flow-label">USDT</span>
              <span className="hero__flow-sub">TRC-20</span>
            </div>
          </div>

          {/* Rate badge */}
          <div className="hero__rate">
            <span className="hero__rate-label">Курс обмена</span>
            <span className="hero__rate-value">1 USDT = {currentRate.toLocaleString('ru-RU')} ₽</span>
          </div>
        </section>

        {/* Order Form */}
        <section className="order-section">
          <div className="order-card">
            <div className="order-card__form">
                <div className="order-card__head">
                  <h2 className="order-card__title">
                    {getText('usdt_title', 'Купить USDT')}
                  </h2>
                  <p className="order-card__subtitle">
                    {getText('usdt_subtitle', 'Заполните форму и оплатите через СБП')}
                  </p>
                </div>

                {!isAuthed && (
                  <div className="auth-banner">
                    <div className="auth-banner__icon">
                      <AppleEmoji emoji="🔐" size={22} />
                    </div>
                    <div className="auth-banner__content">
                      <div className="auth-banner__title">Нужна авторизация</div>
                      <div className="auth-banner__text">Войдите, чтобы совершить покупку</div>
                    </div>
                    <button
                      className="auth-banner__btn"
                      onClick={() => navigate('/login', { state: { from: '/' } })}
                    >
                      Войти
                    </button>
                  </div>
                )}

                {isAuthed && !authUser?.is_fully_verified && (
                  <div className="auth-banner auth-banner--warn">
                    <div className="auth-banner__icon">
                      <AppleEmoji emoji="⏳" size={22} />
                    </div>
                    <div className="auth-banner__content">
                      <div className="auth-banner__title">Верификация не завершена</div>
                      <div className="auth-banner__text">Подтвердите телефон</div>
                    </div>
                    <button
                      className="auth-banner__btn"
                      onClick={() => navigate('/account/verification')}
                    >
                      Перейти
                    </button>
                  </div>
                )}

                {/* Currency mode switch */}
                <div className="currency-switch" role="tablist" aria-label="Валюта ввода">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={inputMode === 'usdt'}
                    className={`currency-switch__btn ${inputMode === 'usdt' ? 'currency-switch__btn--active' : ''}`}
                    onClick={() => setInputMode('usdt')}
                  >
                    USDT
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={inputMode === 'rub'}
                    className={`currency-switch__btn ${inputMode === 'rub' ? 'currency-switch__btn--active' : ''}`}
                    onClick={() => setInputMode('rub')}
                  >
                    Рубли
                  </button>
                </div>

                {/* Amount input (USDT or RUB) */}
                <div className="form-field">
                  <div className="input-wrapper">
                    {inputMode === 'usdt' ? (
                      <>
                        <input
                          type="text"
                          inputMode="decimal"
                          className="input"
                          placeholder="Количество USDT"
                          value={usdtInput}
                          onChange={(e) => setUsdtInput(e.target.value.replace(/[^0-9.]/g, ''))}
                        />
                        <span className="input-icon">
                          <img src={settings['icon_usdt'] || '/usdt.svg'} alt="" width="20" height="20" style={{ borderRadius: '50%' }} />
                        </span>
                      </>
                    ) : (
                      <>
                        <input
                          type="text"
                          inputMode="decimal"
                          className="input"
                          placeholder="Сумма в рублях"
                          value={rubInput}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/[^0-9.]/g, '');
                            setRubInput(raw);
                            const numRub = parseFloat(raw);
                            if (numRub > 0) {
                              const usdt = usdtFromRub(numRub);
                              setUsdtInput(usdt > 0 ? usdt.toFixed(4) : '');
                            } else {
                              setUsdtInput('');
                            }
                          }}
                        />
                        <span className="input-icon">₽</span>
                      </>
                    )}
                  </div>
                  {inputMode === 'rub' && parseFloat(usdtInput) > 0 && (
                    <div className="input-hint">
                      ≈ {parseFloat(usdtInput).toLocaleString('ru-RU', { maximumFractionDigits: 4 })} USDT
                    </div>
                  )}
                  {inputMode === 'usdt' && parseFloat(rubInput) > 0 && (
                    <div className="input-hint">
                      ≈ {parseFloat(rubInput).toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽ к оплате
                    </div>
                  )}
                </div>

                {/* Quick amounts */}
                <div className="quick-amounts">
                  {(inputMode === 'usdt'
                    ? [10, 50, 100, 500, 1000]
                    : [1000, 5000, 10000, 50000, 100000]
                  ).map((amt) => {
                    const activeVal = inputMode === 'usdt' ? parseFloat(usdtInput) : parseFloat(rubInput);
                    return (
                      <button
                        key={amt}
                        className={`quick-amounts__btn ${activeVal === amt ? 'quick-amounts__btn--active' : ''}`}
                        onClick={() => {
                          if (inputMode === 'usdt') {
                            setUsdtInput(amt.toString());
                          } else {
                            setRubInput(amt.toString());
                            const usdt = usdtFromRub(amt);
                            setUsdtInput(usdt > 0 ? usdt.toFixed(4) : '');
                          }
                        }}
                      >
                        {inputMode === 'usdt' ? amt : amt.toLocaleString('ru-RU')}
                      </button>
                    );
                  })}
                </div>

                {/* Wallet Address */}
                <div className="form-field">
                  <WalletSelector
                    value={wallet}
                    onChange={setWallet}
                    enabled={!!authToken && !!authUser}
                  />
                </div>

                {/* Network badge */}
                <div className="network-badge">
                  <span className="network-badge__dot"></span>
                  Сеть: TRC-20 (TRON)
                </div>

                {/* Payment Method */}
                <div className="form-field">
                  <div className="select-wrapper" onClick={() => setIsPaymentOpen(!isPaymentOpen)}>
                    <div className="select">
                      {paymentMethod ? (
                        <span className="select__value">
                          {getPaymentIcon(paymentMethods.find(m => m.code === paymentMethod)!)}
                          {paymentMethods.find(m => m.code === paymentMethod)?.name}
                        </span>
                      ) : (
                        <span className="select__placeholder">Выберите способ оплаты</span>
                      )}
                      <span className={`select__arrow ${isPaymentOpen ? 'select__arrow--open' : ''}`}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M6 9l6 6 6-6"/>
                        </svg>
                      </span>
                    </div>
                    {isPaymentOpen && (
                      <div className="select__dropdown">
                        {paymentMethods.map((method) => (
                          <div
                            key={method.code}
                            className={`select__option ${paymentMethod === method.code ? 'select__option--active' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setPaymentMethod(method.code);
                              setIsPaymentOpen(false);
                            }}
                          >
                            {getPaymentIcon(method)}
                            <span>{method.name}</span>
                            {method.commission_text && (
                              <span className="select__option-commission">{method.commission_text}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Promo Code */}
                <div className="form-field">
                  <div className="promo-wrapper">
                    <input
                      type="text"
                      className="input promo-input"
                      placeholder="Промокод"
                      value={promoCode}
                      onChange={(e) => { setPromoCode(e.target.value); setPromoApplied(null); }}
                    />
                    <button
                      className={`promo-btn ${promoApplied ? 'promo-btn--applied' : ''}`}
                      onClick={handleApplyPromo}
                      disabled={promoLoading || !!promoApplied}
                    >
                      {promoLoading ? '...' : promoApplied ? '✓' : 'Применить'}
                    </button>
                  </div>
                </div>

                {/* Price summary */}
                {price && parseFloat(usdtInput) > 0 && (
                  <div className="price-summary">
                    <div className="price-summary__row">
                      <span>Курс</span>
                      <span>{currentRate.toLocaleString('ru-RU')} ₽/USDT</span>
                    </div>
                    {price.discount > 0 && (
                      <div className="price-summary__row price-summary__row--discount">
                        <span>Скидка за объём</span>
                        <span>-{price.discount.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽</span>
                      </div>
                    )}
                    {commissionsEnabled && (
                      <>
                        <div className="price-summary__row">
                          <span>Комиссия сервиса ({parseFloat(settings['service_commission_percent'] || '10')}%)</span>
                          <span>+{price.service_commission.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽</span>
                        </div>
                        <div className="price-summary__row">
                          <span>Комиссия за перевод (+{parseFloat(settings['usdt_transfer_fee_usdt'] || '2')} USDT)</span>
                          <span>+{price.transfer_fee.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽</span>
                        </div>
                      </>
                    )}
                    {promoApplied && (
                      <div className="price-summary__row price-summary__row--discount">
                        <span>Промокод {promoApplied.code}</span>
                        <span>-{promoApplied.discount.toLocaleString('ru-RU')} ₽</span>
                      </div>
                    )}
                    <div className="price-summary__row price-summary__row--total">
                      <span>Итого</span>
                      <span>{(price.final_price - (promoApplied?.discount || 0)).toLocaleString('ru-RU')} ₽</span>
                    </div>
                  </div>
                )}

                {/* Commission */}
                {commissionsEnabled && (
                  <div className="commission">
                    {getText('commission_text', 'Комиссия сервиса')} <span className="commission__value">{parseFloat(settings['service_commission_percent'] || '10')}%</span>
                  </div>
                )}

                {/* Pay Button */}
                <button
                  className={'pay-btn' + (isFormValid || !isAuthed ? '' : ' pay-btn--inactive')}
                  disabled={isLoading}
                  onClick={handlePay}
                >
                  {isLoading
                    ? 'Создание заказа...'
                    : !isAuthed
                      ? 'Войти, чтобы продолжить'
                      : finalPrice > 0
                        ? `Оплатить ${finalPrice.toLocaleString('ru-RU')} ₽`
                        : 'Оплатить'}
                </button>

                {/* Terms */}
                <p className="terms">
                  Продолжая, вы соглашаетесь с{' '}
                  <a href={settings['terms_link'] || '/terms'}>Пользовательским соглашением</a>
                </p>
            </div>
          </div>
        </section>

        {/* Trust strip */}
        <section className="trust-strip" aria-label="Партнёры и сети">
          <div className="trust-strip__item">
            <img src="/sbp.svg" alt="СБП" className="trust-strip__logo" />
          </div>
          <div className="trust-strip__divider" />
          <div className="trust-strip__item">
            <img src={settings['icon_usdt'] || '/usdt.svg'} alt="" width="24" height="24" />
            <span>Tether USDT</span>
          </div>
          <div className="trust-strip__divider" />
          <div className="trust-strip__item">
            <img src="/tron.svg" alt="" width="24" height="24" />
            <span>TRON TRC-20</span>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="benefits">
          <div className="benefits__grid">
            <div className="benefit-card">
              <div className="benefit-card__icon"><AppleEmoji emoji="⚡" size={52} /></div>
              <h3 className="benefit-card__title">{getText('benefit_1_title', 'Быстро')}</h3>
              <p className="benefit-card__text">{getText('benefit_1_text', 'USDT отправляется в течение 5-15 минут после оплаты')}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-card__icon"><AppleEmoji emoji="🔒" size={52} /></div>
              <h3 className="benefit-card__title">{getText('benefit_2_title', 'Надёжно')}</h3>
              <p className="benefit-card__text">{getText('benefit_2_text', 'Безопасные платежи через СБП')}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-card__icon"><AppleEmoji emoji="💬" size={52} /></div>
              <h3 className="benefit-card__title">{getText('benefit_3_title', 'Поддержка 24/7')}</h3>
              <p className="benefit-card__text">{getText('benefit_3_text', 'Мы всегда на связи в Telegram')}</p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="faq">
          <div className="faq__container">
            <div className="faq__head">
              <h2 className="faq__title">Частые вопросы</h2>
              <p className="faq__subtitle">Всё, что нужно знать перед первым обменом</p>
            </div>
            <div className="faq__list">
              {faqItems.map((item, index) => (
                <div
                  key={index}
                  className={`faq__item ${openFaq === index ? 'faq__item--open' : ''}`}
                >
                  <button
                    className="faq__question"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  >
                    <span className="faq__question-text">{item.q}</span>
                    <span className="faq__chevron" aria-hidden="true">
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                        <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  </button>
                  <div className="faq__answer-wrapper">
                    <div className="faq__answer-inner">
                      <div className="faq__answer">{item.a}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <Toast messages={toasts} onRemove={removeToast} />
    </div>
  );
}
